import { useContext, useState, useEffect } from 'react';
import { useKpiCollection } from '../hooks/useKpiCollection';
import { KlartextEventConfigContext } from '../providers/klartextEventConfig';
import ListeningView from './ListeningView';
import { StatisticsKpiPoint, StatisticsKpiValue } from '@configTypes/kpiStatistics';
import { useGlobalState } from '../providers/globalState';
import { KeywordsView } from './KeywordsView';

export function StatisticsView() {
  const speechConfig = useContext(KlartextEventConfigContext);
  const globalState = useGlobalState();
  const { dispatch } = globalState ?? {};

  const [democratStatistics, setDemocratStatistics] = useState<StatisticsKpiValue | null>(null);
  const [republicanStatistics, setRepublicanStatistics] = useState<StatisticsKpiValue | null>(null);
  const [republicanStatsKpis] = useKpiCollection<StatisticsKpiPoint[]>({
    eventId: speechConfig?.eventId ?? null,
    kpiName: `Statistics_${speechConfig?.frontendSpecificConfig?.participants.republican.transcriptAlias}`,
    initialState: [] as StatisticsKpiPoint[],
  });

  const [democratStatsKpis] = useKpiCollection<StatisticsKpiPoint[]>({
    eventId: speechConfig?.eventId ?? null,
    kpiName: `Statistics_${speechConfig?.frontendSpecificConfig?.participants.democrat.transcriptAlias}`,
    initialState: [] as StatisticsKpiPoint[],
  });

  useEffect(() => {
    const newLatestStats = republicanStatsKpis.at(-1)?.value ?? null;

    if (!newLatestStats) {
      return;
    }
    setRepublicanStatistics(newLatestStats);
  }, [republicanStatsKpis]);

  useEffect(() => {
    const newLatestStats = democratStatsKpis.at(-1)?.value ?? null;

    if (!newLatestStats) {
      return;
    }
    setDemocratStatistics(newLatestStats);
  }, [democratStatsKpis]);

  if (!republicanStatistics || !democratStatistics) {
    return <ListeningView />;
  }
  return (
    <>
      <div className="mt-10 space-y-2 px-1">
        <strong className="font-medium leading-none">Words per sentence:</strong>
        <EloquenceItem
          republicanValue={republicanStatistics.avgWordsPerSentence.toFixed(1)}
          democratValue={democratStatistics.avgWordsPerSentence.toFixed(1)}
        />
      </div>
      <div className="mt-10 space-y-2 px-1">
        <strong className="font-medium leading-none">Unique words per 100 words:</strong>
        <EloquenceItem
          republicanValue={republicanStatistics.avgUniqueWordsPerHundred.toFixed(0)}
          democratValue={democratStatistics.avgUniqueWordsPerHundred.toFixed(0)}
        />
      </div>
      <div className="mt-10 space-y-8 px-1">
        <strong className="font-medium leading-none">Most used terms:</strong>
        <KeywordsView />
      </div>
    </>
  );
}

export const EloquenceItem = ({
  republicanValue,
  democratValue,
}: {
  republicanValue: string;
  democratValue: string;
}) => {
  return (
    <div className="grid grid-cols-2">
      <div className="flex flex-col">
        <span className="text-[5.625rem] font-bold leading-none text-democrat">{democratValue}</span>
      </div>
      <div className="flex flex-col text-right">
        <span className="text-[5.625rem] font-bold leading-none text-republican">{republicanValue}</span>
      </div>
    </div>
  );
};
